import React, { useEffect, useState } from 'react';
import { UsuarioFacade, Usuario, UsuarioDTO, Role, RoleFacade } from '../../facades/UsuarioFacade';
import Sidebar from '../Sidebar';

const initialState: UsuarioDTO = {
    nome: '',
    login: '',
    senha: '',
    roleIds: []
};

export const UsuariosList: React.FC = () => {
    const [usuarios, setUsuarios] = useState<Usuario[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [sucesso, setSucesso] = useState<string | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editandoId, setEditandoId] = useState<number | null>(null);
    const [formData, setFormData] = useState<UsuarioDTO>(initialState);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        carregarDados();
    }, []);

    const carregarDados = async () => {
        try {
            setLoading(true);
            const [usuariosData, rolesData] = await Promise.all([
                UsuarioFacade.listarTodos(),
                RoleFacade.listarTodos()
            ]);
            setUsuarios(usuariosData || []);
            setRoles(rolesData || []);
        } catch (error) {
            setErro("Não foi possível carregar os dados. Acesso negado ou servidor indisponível.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRoleChange = (roleId: number) => {
        setFormData(prev => {
            const newRoleIds = prev.roleIds.includes(roleId)
                ? prev.roleIds.filter(id => id !== roleId)
                : [...prev.roleIds, roleId];
            return { ...prev, roleIds: newRoleIds };
        });
    };

    const abrirModalNovo = () => {
        setFormData(initialState);
        setEditandoId(null);
        setIsModalOpen(true);
    };

    const abrirModalEditar = (usuario: Usuario) => {
        setFormData({
            nome: usuario.nome,
            login: usuario.login,
            senha: '', // Senha não é preenchida na edição
            roleIds: usuario.roles.map(r => r.id)
        });
        setEditandoId(usuario.id);
        setIsModalOpen(true);
    };

    const handleSalvar = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.roleIds.length === 0) {
            setErro("Selecione ao menos um perfil para o usuário.");
            return;
        }
        setSalvando(true);
        try {
            if (editandoId) {
                await UsuarioFacade.atualizar(editandoId, formData);
                setSucesso("Usuário atualizado com sucesso!");
            } else {
                await UsuarioFacade.criar(formData);
                setSucesso("Usuário criado com sucesso!");
            }
            setIsModalOpen(false);
            await carregarDados();
        } catch (error) {
            setErro("Erro ao salvar. Verifique se o login já não está em uso.");
        } finally {
            setSalvando(false);
        }
    };

    const handleExcluir = async (id: number) => {
        if (window.confirm("Tem certeza que deseja excluir este usuário?")) {
            try {
                await UsuarioFacade.excluir(id);
                setSucesso("Usuário excluído com sucesso!");
                await carregarDados();
            } catch (error) {
                setErro("Não foi possível excluir o usuário.");
            }
        }
    };

    return (
        <Sidebar>
            <header className="mb-8">
                <h2 className="text-3xl font-extrabold text-gray-800">Gestão de Usuários</h2>
                <p className="text-gray-500">Crie, edite e gerencie os usuários e suas permissões.</p>
            </header>

            <button onClick={abrirModalNovo} className="mb-6 bg-blue-800 text-white font-bold py-2 px-4 rounded-lg">
                Novo Usuário
            </button>

            {erro && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{erro}</div>}
            {sucesso && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">{sucesso}</div>}

            {loading ? <p>Carregando...</p> : (
                <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Login</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Perfis</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {usuarios.map(user => (
                                <tr key={user.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">{user.nome}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">{user.login}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {user.roles.map(role => (
                                            <span key={role.id} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 mr-1">
                                                {role.nome.replace('ROLE_', '')}
                                            </span>
                                        ))}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => abrirModalEditar(user)} className="text-indigo-600 hover:text-indigo-900 mr-4">Editar</button>
                                        <button onClick={() => handleExcluir(user.id)} className="text-red-600 hover:text-red-900">Excluir</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">{editandoId ? 'Editar Usuário' : 'Novo Usuário'}</h3>
                        <form onSubmit={handleSalvar} className="mt-4">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700">Nome</label>
                                    <input type="text" name="nome" value={formData.nome} onChange={handleInputChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"/>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700">Login</label>
                                    <input type="text" name="login" value={formData.login} onChange={handleInputChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"/>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700">Senha {editandoId && '(Deixe em branco para não alterar)'}</label>
                                    <input type="password" name="senha" value={formData.senha} onChange={handleInputChange} required={!editandoId} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"/>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700">Perfis</label>
                                    <div className="mt-2 space-y-2">
                                        {roles && roles.length > 0 ? (
                                            roles.map(role => (
                                                <div key={role.id} className="flex items-center">
                                                    <input
                                                        id={`role-${role.id}`}
                                                        type="checkbox"
                                                        checked={formData.roleIds.includes(role.id)}
                                                        onChange={() => handleRoleChange(role.id)}
                                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                    />
                                                    <label htmlFor={`role-${role.id}`} className="ml-3 block text-sm text-gray-900">
                                                        {role.nome.replace('ROLE_', '')}
                                                    </label>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500">Carregando perfis...</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md">Cancelar</button>
                                <button type="submit" disabled={salvando} className="bg-blue-800 text-white px-4 py-2 rounded-md">{salvando ? 'Salvando...' : 'Salvar'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Sidebar>
    );
};